import { useCallback, useEffect, useMemo, useRef } from "react";
import { FormikProps } from "formik";
import { isAxiosError } from "axios";
import { ErrorMessageSchema } from "@common/schemas/error.schema";
import { suggestNextAccountCodeText } from "@helpers/suggestNextAccountCode";
import {
    AccountClass,
    AccountSubtype,
    AccountType,
} from "@common/constants/domains/accounting/accounts";
import {
    getAvailableAccountTypes,
    getDefaultSubtypeForType,
    isAccountSubtypeValidForType,
    isAccountTypeValidForClass,
} from "@common/constants/domains/accounting/accountTypeHelpers";
import { Entity } from "@common/types/entity";
import {
    Account,
    AccountGroup,
} from "@common/types/domains/accounting/account";
import { filterGroupsBySelectableType } from "@components/forms/GroupSelectField/selectableGroupType";
import {
    normalizeCodeText,
    useChangeAccountCodeText,
} from "./useCoAAccountFormHelpers";

const FALLBACK_CREATE_ERROR = "Failed to create account";

// The backend rejects conflicting names, codes and subtypes with a message that
// tells the user what to change; a bare fallback leaves them retrying blindly.
export function getCreateAccountErrorMessage(error: unknown): string {
    if (!isAxiosError(error) || error.response?.status !== 400) {
        return FALLBACK_CREATE_ERROR;
    }
    const parsed = ErrorMessageSchema.safeParse(error.response.data);
    return parsed.success ? parsed.data.message : FALLBACK_CREATE_ERROR;
}

export interface CoAAccountFormValues {
    name: string;
    type: AccountType;
    subtype: AccountSubtype;
    code: number | null;
    codeText: string | null;
    entityIds: number[];
    parentGroupId: string | null;
}

export const buildCreateAccountInitialValues = ({
    accountName,
    type,
    subtype,
    availableClassifications,
    entityIds,
    businessEntities,
    parentGroupId,
}: {
    accountName: string;
    type: AccountType;
    subtype: AccountSubtype;
    availableClassifications?: AccountClass[];
    entityIds?: number[];
    businessEntities: Array<Pick<Entity, "id" | "legalType">>;
    parentGroupId?: string | null;
}): CoAAccountFormValues => {
    let validatedType = type;
    let validatedSubtype = subtype;

    // Validate type against available classifications
    if (availableClassifications && availableClassifications.length > 0) {
        const isTypeValid = availableClassifications.some((classification) =>
            isAccountTypeValidForClass(validatedType, classification),
        );
        if (!isTypeValid) {
            // Default to first available type
            const availableTypes = getAvailableAccountTypes(
                availableClassifications,
            );
            validatedType = availableTypes[0];
        }
    }

    if (!isAccountSubtypeValidForType(validatedSubtype, validatedType)) {
        // Default to first valid subtype for this type
        const selectedInitialEntities = businessEntities.filter((entity) =>
            entityIds?.includes(entity.id),
        );
        for (const entity of selectedInitialEntities) {
            if (entity.legalType) {
                validatedSubtype = getDefaultSubtypeForType(
                    entity.legalType,
                    validatedType,
                );
            }
        }
    }

    return {
        name: accountName,
        type: validatedType,
        subtype: validatedSubtype,
        code: null,
        codeText: null,
        entityIds: entityIds?.length
            ? entityIds
            : businessEntities.map((entity) => entity.id),
        parentGroupId: parentGroupId ?? null,
    };
};

// Validation must run against this wire payload, not the raw formik values —
// they carry a placeholder `code: null` the request never includes.
export const buildCreateAccountBody = (
    values: CoAAccountFormValues,
    { groups }: { groups?: AccountGroup[] },
) => {
    const selectedEntityId = values.entityIds[0];
    const availableGroups = filterGroupsBySelectableType(
        groups ?? [],
        values.type,
    ).filter((group) => group.entityId === selectedEntityId);
    const hasAllowedParentGroup =
        values.entityIds.length === 1 &&
        (values.parentGroupId === null ||
            availableGroups.some((group) => group.id === values.parentGroupId));

    return {
        name: values.name,
        type: values.type,
        entityIds: values.entityIds,
        ...(hasAllowedParentGroup
            ? { parentGroupId: values.parentGroupId }
            : {}),
        codeText: normalizeCodeText(values.codeText),
    };
};

export const getCreateAccountGroupFieldState = ({
    groups,
    entityIds,
    parentGroupId,
    type,
}: {
    groups: AccountGroup[];
    entityIds: number[];
    parentGroupId: string | null;
    type: AccountType;
}) => {
    if (entityIds.length !== 1) {
        return {
            groups: [],
            parentGroupId: null,
            isDisabled: true,
        };
    }

    const availableGroups = filterGroupsBySelectableType(groups, type).filter(
        (group) => group.entityId === entityIds[0],
    );
    const isParentGroupAvailable =
        parentGroupId === null ||
        availableGroups.some((group) => group.id === parentGroupId);

    return {
        groups: availableGroups,
        parentGroupId: isParentGroupAvailable ? parentGroupId : null,
        isDisabled: false,
    };
};

interface UseCoAAccountFormHelpersParams {
    selectedEntities: Array<Pick<Entity, "id" | "legalType">>;
    accounts: Array<Account & { entityId: number }>;
    formik: FormikProps<CoAAccountFormValues>;
    availableClassifications?: AccountClass[];
    // The group the request will actually carry, as resolved by
    // `getCreateAccountGroupFieldState`: a stale or multi-entity selection is
    // dropped from the payload, so the duplicate-name check must ignore it too.
    effectiveParentGroupId: string | null;
}

export function useCoAAccountCreateFormHelpers({
    selectedEntities,
    accounts,
    formik,
    availableClassifications,
    effectiveParentGroupId,
}: UseCoAAccountFormHelpersParams) {
    const nameInputRef = useRef<HTMLInputElement>(null);

    const changeAccountCodeText = useChangeAccountCodeText(formik);

    const { entityIds, type: formType, codeText } = formik.values;
    const { setFieldValue } = formik;

    const derivedCodeText = useMemo(
        () =>
            suggestNextAccountCodeText({
                accounts,
                targets: entityIds.map((entityId) => ({
                    entityId,
                    parentGroupId: effectiveParentGroupId,
                })),
                type: formType,
            }),
        [accounts, entityIds, effectiveParentGroupId, formType],
    );

    // Remembered value, not touched: follow the scope only while the field still
    // equals the last suggestion we wrote (including `null`).
    const lastSuggestionRef = useRef<string | null>(null);
    const codeTextRef = useRef(codeText);
    codeTextRef.current = codeText;
    const initialValuesRef = useRef(formik.initialValues);

    useEffect(() => {
        // enableReinitialize resets codeText to the initial null without going
        // through this effect. Treat that as a fresh form, not a user clear.
        if (initialValuesRef.current !== formik.initialValues) {
            initialValuesRef.current = formik.initialValues;
            lastSuggestionRef.current = normalizeCodeText(
                formik.initialValues.codeText,
            );
        }
        if (
            normalizeCodeText(codeTextRef.current) === lastSuggestionRef.current
        ) {
            lastSuggestionRef.current = derivedCodeText;
            setFieldValue("codeText", derivedCodeText);
        }
    }, [derivedCodeText, setFieldValue, formik.initialValues]);

    const changeAccountType = useCallback(
        (newType: AccountType) => {
            formik.setFieldValue("type", newType);
            // Groups are exact-type homogeneous, so any type change clears the parent.
            formik.setFieldValue("parentGroupId", null);
        },
        [formik],
    );

    const messages: Array<{ entityId: number; message: string }> =
        useMemo(() => {
            const result: Array<{ entityId: number; message: string }> = [];
            for (const entity of selectedEntities) {
                const accountsForEntity = accounts.filter(
                    (account) => account.entityId === entity.id,
                );
                // Mirror the backend: a name only has to be unique among the
                // siblings of the group the account is being created in.
                const siblings = accountsForEntity.filter(
                    (a) =>
                        a.type === formik.values.type &&
                        (a.parentGroupId ?? null) === effectiveParentGroupId,
                );
                const hasSameName = siblings.some(
                    (a) =>
                        a.name.trim().toLowerCase() ===
                        formik.values.name.trim().toLowerCase(),
                );

                if (hasSameName) {
                    result.push({
                        entityId: entity.id,
                        message:
                            effectiveParentGroupId === null
                                ? "Account name already exists."
                                : "Account name already exists in this group.",
                    });
                }
            }

            return result;
        }, [
            selectedEntities,
            accounts,
            formik.values.type,
            formik.values.name,
            effectiveParentGroupId,
        ]);

    const availableAccountTypes = useMemo(
        () => getAvailableAccountTypes(availableClassifications),
        [availableClassifications],
    );

    return {
        nameInputRef,
        messages,
        availableAccountTypes,
        changeAccountType,
        changeAccountCodeText,
    };
}
